import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as reposApi from '@/api/repos.api';

/**
 * Query keys for repositories
 */
export const repoKeys = {
  all: ['repositories'] as const,
  lists: () => [...repoKeys.all, 'list'] as const,
  list: (params: reposApi.RepoQueryParams) => [...repoKeys.lists(), params] as const,
  details: () => [...repoKeys.all, 'detail'] as const,
  detail: (id: string) => [...repoKeys.details(), id] as const,
  commits: (id: string) => [...repoKeys.detail(id), 'commits'] as const,
};

/**
 * Fetch repositories with filters
 */
export function useRepositories(params?: reposApi.RepoQueryParams) {
  return useQuery({
    queryKey: repoKeys.list(params || {}),
    queryFn: () => reposApi.getRepositories(params),
  });
}

/**
 * Fetch a single repository
 */
export function useRepository(id: string) {
  return useQuery({
    queryKey: repoKeys.detail(id),
    queryFn: () => reposApi.getRepository(id),
    enabled: !!id,
  });
}

/**
 * Fetch commits for a repository
 */
export function useRepositoryCommits(id: string, limit?: number) {
  return useQuery({
    queryKey: repoKeys.commits(id),
    queryFn: () => reposApi.getRepositoryCommits(id, limit),
    enabled: !!id,
  });
}

/**
 * Update repository mutation
 */
export function useUpdateRepository() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: reposApi.UpdateRepoData }) =>
      reposApi.updateRepository(id, data),
    onSuccess: (updatedRepo) => {
      queryClient.invalidateQueries({ queryKey: repoKeys.lists() });
      queryClient.setQueryData(repoKeys.detail(updatedRepo.id), updatedRepo);
    },
  });
}

/**
 * Sync repository mutation
 */
export function useSyncRepository() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, commitLimit }: { id: string; commitLimit?: number }) =>
      reposApi.syncRepository(id, commitLimit),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: repoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: repoKeys.commits(id) });
      queryClient.invalidateQueries({ queryKey: ['commits'] });
    },
  });
}

/**
 * Register webhook mutation
 */
export function useRegisterWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repoId: string) => reposApi.registerWebhook(repoId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: repoKeys.detail(result.repoId) });
      queryClient.invalidateQueries({ queryKey: repoKeys.lists() });
    },
  });
}

/**
 * Unregister webhook mutation
 */
export function useUnregisterWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (repoId: string) => reposApi.unregisterWebhook(repoId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: repoKeys.detail(result.repoId) });
      queryClient.invalidateQueries({ queryKey: repoKeys.lists() });
    },
  });
}
